import type { PluginWebManifest } from '@reflex/plugin-api/web';

const PKG = '@reflex/plugin-docs';
const DOCS_BASE_PATH = '/docs';
const DOCS_INDEX_LABEL = 'Docs';
const DOCS_NAV_LABEL = 'Documentation';
const DOCS_ARTICLE_LABEL = 'Article';
const DOCS_ICON = 'BookOpen';

/**
 * Web manifest for the docs plugin. Lives in its own module so the
 * plugin definition (`src/index.ts`) and the web registration
 * (`src/web/register.ts`) can both import it without dragging the
 * server modules into the browser bundle (mirrors `plugin-marketing`'s
 * `web-manifest.ts` pattern). The SPA reads it via
 * `createWebRegistrationFromManifest` to resolve public docs routes
 * statically — anonymous visitors hit `/docs` / `/docs/:slug+` before
 * the auth-gated `GET /api/config/plugins` fetch resolves, so static
 * resolution is required for the live link to work without a session.
 */
export const docsWeb: PluginWebManifest = {
  routes: [
    {
      path: DOCS_BASE_PATH,
      componentPath: `${PKG}/web/DocsIndexPage`,
      public: true,
      docs: {
        label: DOCS_INDEX_LABEL,
        description: 'Browse the Reflex documentation.',
        icon: DOCS_ICON,
        order: 0,
      },
    },
    {
      path: `${DOCS_BASE_PATH}/*`,
      componentPath: `${PKG}/web/DocsPage`,
      public: true,
      docs: {
        label: DOCS_ARTICLE_LABEL,
        description: 'A single Reflex documentation page.',
        icon: DOCS_ICON,
        order: 10,
      },
    },
  ],
  // Docs renders at the public, root-level URL (`/docs`) — same surface
  // for anonymous and signed-in readers (PRD-8a / ADR-002). `rootLevel:
  // true` keeps the in-app nav link from prepending the active org slug
  // so signed-in users navigating from the account menu end up at the same
  // URL anonymous visitors share. `openInNewWindow: true` makes the link
  // open docs in a separate browser tab rather than replacing the current
  // app view. The link is pinned under every takeover nav
  // (`group: 'takeover-footer'`) alongside Send feedback and Releases: it
  // leaves the app, so it is not an account page.
  navItems: [
    {
      to: DOCS_BASE_PATH,
      label: DOCS_NAV_LABEL,
      icon: DOCS_ICON,
      group: 'takeover-footer',
      rootLevel: true,
      openInNewWindow: true,
      // High weight keeps Documentation last in the footer, below Send
      // feedback and Releases.
      order: 100,
    },
  ],
  commands: [
    {
      to: DOCS_BASE_PATH,
      label: DOCS_INDEX_LABEL,
      icon: DOCS_ICON,
      group: 'navigation',
      rootLevel: true,
    },
  ],
  overviewCards: [
    {
      to: DOCS_BASE_PATH,
      label: DOCS_INDEX_LABEL,
      icon: DOCS_ICON,
      description: 'Browse project documentation, architecture notes, and integration guides.',
      details:
        'Public docs surface backed by the validated docs index. Anonymous and signed-in readers see the same JSON-block pages; live-aware blocks (PRD-9a) light up workspace data when signed in.',
      features: [
        'Block-based public docs (`DocsLayout` outside `MarketingLayout`)',
        'Per-page SSR metadata (title / description / og:*)',
        'Sidebar grouped by folder',
        'Per-page table of contents with active section tracking',
      ],
      group: 'plugins',
    },
  ],
};

export const DOCS_BASE_PATH_VALUE = DOCS_BASE_PATH;
